<template>
  <div class="max-w-6xl mx-auto">
    <h1 class="text-2xl font-bold mb-6">Edit Tool</h1>
    
    <div class="flex flex-col lg:flex-row gap-6">
      <!-- Left Panel - Edit Form -->
      <div class="lg:w-1/2">
        <div class="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 mb-8">
          <div class="p-6">
            <form @submit.prevent="saveTool">
              <!-- Basic Info -->
              <div class="mb-6">
                <h2 class="text-lg font-semibold mb-3 pb-2 border-b border-gray-200">Basic Information</h2>
                
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Tool Name</label>
                  <input 
                    v-model="tool.name" 
                    class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    required
                  />
                </div>
                
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea 
                    v-model="tool.description" 
                    rows="3"
                    class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    required
                  ></textarea>
                </div>
                
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select 
                    v-model="tool.category" 
                    class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    required
                  >
                    <option value="">Select a category...</option>
                    <option value="Email">Email</option>
                    <option value="Career">Career</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Social Media">Social Media</option>
                    <option value="Legal">Legal</option>
                    <option value="Content">Content</option>
                    <option value="Technical">Technical</option>
                    <option value="Business">Business</option>
                    <option value="SEO">SEO</option>
                  </select>
                </div>
                
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Category Color</label>
                  <select 
                    v-model="tool.category_color" 
                    class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  >
                    <option value="">Select a color...</option>
                    <option value="blue">Blue</option>
                    <option value="green">Green</option>
                    <option value="orange">Orange</option>
                    <option value="rose">Rose</option>
                    <option value="violet">Violet</option>
                    <option value="amber">Amber</option>
                    <option value="pink">Pink</option>
                    <option value="indigo">Indigo</option>
                    <option value="red">Red</option>
                    <option value="purple">Purple</option>
                  </select>
                </div>
                
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Icon (Font Awesome class)</label>
                  <input 
                    v-model="tool.icon" 
                    class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    placeholder="fas fa-envelope"
                  />
                  <p class="mt-1 text-xs text-gray-500">
                    Example: fas fa-envelope, fab fa-twitter (include the full class)
                  </p>
                </div>
              </div>
              
              <!-- Input Configuration -->
              <div class="mb-6">
                <h2 class="text-lg font-semibold mb-3 pb-2 border-b border-gray-200">Input Configuration</h2>
                
                <div class="mb-4">
                  <label class="inline-flex items-center">
                    <input type="checkbox" v-model="tool.prompt_required" class="rounded border-gray-300">
                    <span class="ml-2">Requires prompt details</span>
                  </label>
                  <p class="mt-1 text-xs text-gray-500">
                    If checked, users will need to provide details about what they want to create.
                  </p>
                </div>
                
                <div class="mb-4" v-if="tool.prompt_required">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Prompt Label</label>
                  <input 
                    v-model="tool.prompt_label" 
                    class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    placeholder="What would you like to create?"
                  />
                </div>
                
                <div class="mb-4">
                  <label class="inline-flex items-center">
                    <input type="checkbox" v-model="tool.input_required" class="rounded border-gray-300">
                    <span class="ml-2">Requires input text</span>
                  </label>
                </div>
                
                <div class="mb-4" v-if="tool.input_required">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Input Label</label>
                  <input 
                    v-model="tool.input_label" 
                    class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    placeholder="Enter your text here"
                    required
                  />
                </div>
                
                <div class="mb-4" v-if="tool.input_required">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Input Instructions</label>
                  <textarea 
                    v-model="tool.input_instructions" 
                    rows="2"
                    class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  ></textarea>
                </div>
                
                <div class="mb-4" v-if="tool.input_required">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Input Placeholder</label>
                  <textarea 
                    v-model="tool.input_placeholder" 
                    rows="2"
                    class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  ></textarea>
                </div>
                
                <div class="mb-4" v-if="tool.input_required">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Input Example</label>
                  <textarea 
                    v-model="tool.input_example" 
                    rows="2"
                    class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  ></textarea>
                </div>
                
                <div class="mb-4">
                  <label class="inline-flex items-center">
                    <input type="checkbox" v-model="tool.tone_required" class="rounded border-gray-300">
                    <span class="ml-2">Allow tone selection</span>
                  </label>
                </div>
              </div>
              
              <!-- Output Configuration -->
              <div class="mb-6">
                <h2 class="text-lg font-semibold mb-3 pb-2 border-b border-gray-200">Output Configuration</h2>
                
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Output Description</label>
                  <input 
                    v-model="tool.output_description" 
                    class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    placeholder="Your Generated Content"
                  />
                </div>
                
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Action Button Label</label>
                  <input 
                    v-model="tool.action_label" 
                    class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    placeholder="Generate Content"
                  />
                </div>
                
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-1">UI Theme</label>
                  <select 
                    v-model="tool.ui_theme" 
                    class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  >
                    <option value="">None</option>
                    <option value="legal">Legal</option>
                    <option value="email">Email</option>
                    <option value="career">Career</option>
                    <option value="social">Social Media</option>
                    <option value="marketing">Marketing</option>
                    <option value="content">Content</option>
                    <option value="technical">Technical</option>
                    <option value="business">Business</option>
                    <option value="seo">SEO</option>
                  </select>
                </div>
              </div>
              
              <!-- Advanced Configuration -->
              <div class="mb-6">
                <h2 class="text-lg font-semibold mb-3 pb-2 border-b border-gray-200">Advanced Configuration</h2>
                
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Prompt Template</label>
                  <textarea 
                    v-model="tool.prompt_template" 
                    rows="4"
                    class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 font-mono text-sm"
                    required
                  ></textarea>
                  <p class="mt-1 text-xs text-gray-500" v-pre>
                    Available placeholders: {{tool_name}}, {{prompt_details}}, {{input}}, {{tone}}
                  </p>
                </div>
                
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Template Type</label>
                  <select 
                    v-model="tool.template_type" 
                    class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  >
                    <option value="">None (Custom Tool)</option>
                    <option value="converter">Content Converter</option>
                    <option value="generator">Content Generator</option>
                    <option value="simplifier">Content Simplifier</option>
                    <option value="formatter">Content Formatter</option>
                    <option value="summarizer">Content Summarizer</option>
                  </select>
                </div>
              </div>
              
              <!-- Tips Section -->
              <div class="mb-6">
                <h2 class="text-lg font-semibold mb-3 pb-2 border-b border-gray-200">Usage Tips</h2>
                
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Tips for Users</label>
                  <div v-for="(tip, index) in tips" :key="index" class="flex items-center mb-2">
                    <input 
                      v-model="tips[index]" 
                      class="flex-grow rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      placeholder="Add a helpful tip"
                    />
                    <button 
                      type="button" 
                      @click="removeTip(index)" 
                      class="ml-2 text-red-500 hover:text-red-700"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                  <button 
                    type="button" 
                    @click="addTip" 
                    class="mt-2 text-blue-500 hover:text-blue-700 flex items-center text-sm"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Tip
                  </button>
                </div>
              </div>
              
              <div class="mt-6">
                <button 
                  type="submit" 
                  class="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md"
                  :disabled="isSaving"
                >
                  {{ isSaving ? 'Saving...' : 'Save Tool' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      
      <!-- Right Panel - Live Preview -->
      <div class="lg:w-1/2">
        <ToolPreview :tool="tool" :tips="tips" />
      </div>
    </div>
  </div>
</template>

<script>
import ToolPreview from './ToolPreview.vue'

export default {
  name: 'EditToolForm',
  components: {
    ToolPreview
  },
  props: {
    tool: {
      type: Object,
      required: true
    },
    service: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      tips: [''],
      isSaving: false
    }
  },
  created() {
    // Clone the initial tool data to avoid directly modifying props
    // this.tool = { ...this.initialTool }
    console.log("Created")
    console.log(this.tool)
    console.log(this.tool.tips)
    
    // Handle tips array
    if (this.tool.tips && Array.isArray(this.tool.tips)) {
      this.tips = this.tool.tips.length > 0 ? [...this.tool.tips] : ['']
    } else if (this.tool.tips && typeof this.tool.tips === 'object') {
      this.tips = Object.values(this.tool.tips)
    } else {
      this.tips = ['']
    }
  },
  methods: {
    saveTool() {
      this.isSaving = true
      
      // Process tips array - remove empty tips
      const filteredTips = this.tips.filter(tip => tip.trim() !== '')
      
      const request = {
        tool: {
          ...this.tool,
          tips: filteredTips
        }
      }

      this.service.update(this.tool.slug, request)
        .then((response) => {
          // Redirect to tools index after saving
          window.location.href = '/tools'
        })
        .catch((error) => {
          console.error('Error saving tool:', error)
          alert('There was an error saving your tool. Please try again.')
        })
        .finally(() => {
          this.isSaving = false
        })
    },
    
    addTip() {
      this.tips.push('')
    },
    
    removeTip(index) {
      if (this.tips.length > 1) {
        this.tips.splice(index, 1)
      } else {
        this.tips = ['']
      }
    }
  }
}
</script> 